import React, { useEffect, useRef, useState } from "react";

const WaveformPlayer = ({ audioSrc, onPlayStateChange }) => {
  const audioRef = useRef(null);
  const waveformRef = useRef(null);
  const [bars, setBars] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const animatingRef = useRef(false);
  const barsRef = useRef([]);

  // Initialize bars
  useEffect(() => {
    const tempBars = [];
    for (let i = 0; i < 30; i++) {
      const baseHeight = 40 + Math.random() * 30;
      tempBars.push({ baseHeight });
    }
    barsRef.current = tempBars;
    setBars(tempBars);
  }, []);

  // Set comfortable default volume (not too loud)
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = 0.25;
    }
    return () => {
      animatingRef.current = false;
      if (audio) {
        audio.pause();
      }
    };
  }, []);

  // Animate bars
  const animate = () => {
    if (!animatingRef.current || !waveformRef.current) return;
    const barElements = waveformRef.current.children;
    barsRef.current.forEach((bar, i) => {
      if (barElements[i]) {
        const change = (Math.random() - 0.5) * 200;
        barElements[i].style.height = `${Math.max(5, bar.baseHeight + change)}px`;
      }
    });
    requestAnimationFrame(animate);
  };

  // Play/pause handler (only plays when user explicitly clicks)
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || !waveformRef.current) return;
    const barElements = waveformRef.current.children;

    if (audio.paused) {
      audio.volume = 0.25;
      audio.play().then(() => {
        animatingRef.current = true;
        setIsPlaying(true);
        animate();
        if (onPlayStateChange) onPlayStateChange(true);
      }).catch(err => {
        console.debug("Audio play prevented:", err);
      });
    } else {
      audio.pause();
      animatingRef.current = false;
      setIsPlaying(false);
      // shrink bars down to a line
      for (let i = 0; i < bars.length; i++) {
        if (barElements[i]) {
          barElements[i].style.height = "5px";
        }
      }
      if (onPlayStateChange) onPlayStateChange(false);
    }
  };

  // Reset when audio ends
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleEnded = () => {
      animatingRef.current = false;
      setIsPlaying(false);
      if (waveformRef.current) {
        const barElements = waveformRef.current.children;
        for (let i = 0; i < bars.length; i++) {
          if (barElements[i]) {
            barElements[i].style.height = "5px";
          }
        }
      }
      if (onPlayStateChange) onPlayStateChange(false);
    };
    audio.addEventListener("ended", handleEnded);
    return () => audio.removeEventListener("ended", handleEnded);
  }, [bars, onPlayStateChange]);

  return (
    <div>
      <div
        ref={waveformRef}
        onClick={togglePlay}
        title={isPlaying ? "Click to pause audio" : "Click to play audio"}
        role="button"
        tabIndex={0}
        aria-label={isPlaying ? "Pause background music" : "Play background music"}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            togglePlay();
          }
        }}
        style={{
          width: 200,
          height: 150,
          display: "flex",
          alignItems: "center",
          gap: 2,
          marginBottom: 20,
          cursor: "pointer",
          position: "relative",
          borderRadius: 4,
          padding: 0,
          scale: 0.4,
        }}
      >
        {bars.map((bar, i) => (
          <div
            key={i}
            style={{
              width: 6,
              background: "linear-gradient(to top, #ffffffff, #ccccccff)",
              height: `${bar.baseHeight}px`,
              transformOrigin: "center center",
              transition: "height 0.2s",
            }}
          />
        ))}
      </div>
      <audio ref={audioRef} src={audioSrc} loop></audio>
    </div>
  );
};

export default WaveformPlayer;
